import * as _ from "lodash";
import { DimensionType, IHierarchy, IHierarchyLevel, IHierarchyNode } from "../PA.Core/PA.Core.IF.Hierarchy";
import { WB_CustomerRow, WB_PlanRow } from "../PA.Core/PAdbSchemaRows";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { IMessageProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { esWB_Customer, esWB_Plan } from '../PA.Core/PAdbSchema'
import { SourceCustomerService } from './SourceCustomerService'

export interface CreateOptions {
    dbContext?: PAdbContext
}

export class PEAPlanCustomerHierarchy implements IHierarchy {
    private wbPlanId: number;
    private planRow: WB_PlanRow
    private dbContext: PAdbContext;

    private dctHierarchyLevels: { [LevelId: string]: IHierarchyLevel };
    private dctNodes: { [NodeId: string]: IHierarchyNode };
    private dctNodesByLevel: { [LevelId: string]: IHierarchyNode[] };

    private dctNodeParentByChildId: { [NodeId: string]: IHierarchyNode };
    private dctNodeChildrenByParentId: { [NodeId: string]: IHierarchyNode[] };
    private arrTopLevelNodes: IHierarchyNode[];

    private _loadMessageProvider: MessageProvider;
    private _OnLoadComplete: Promise<void>;
    private _bLoaded: boolean;

    constructor(wbPlanId: number, options?: CreateOptions) {
        this.wbPlanId = wbPlanId;
        this._bLoaded = false;
        this._loadMessageProvider = new MessageProvider();
        this.dctHierarchyLevels = {};
        this.dctNodes = {};
        this.dctNodesByLevel = {};
        this.dctNodeParentByChildId = {};
        this.dctNodeChildrenByParentId = {};
        this.arrTopLevelNodes = [];

        const opts = options || {}
        this.dbContext = opts?.dbContext

        if (_.isEmpty(this.dbContext))
            this.dbContext = new PAdbContext()

    }

    get DimensionType(): DimensionType {
        return DimensionType.Customer;
    }

    get HierarchyId(): string {
        return this.wbPlanId + "";
    }

    get HierarchyIdUI(): string {
        return this.planRow?.cif?.PLAN_ID || 'Not Set';
    }

    get HierarchyName(): string {
        return this.planRow?.cif?.PLAN_NAME || 'Not Set';
    }

    get bHasSortOrder(): boolean {
        return true;
    }

    get LoadMessageProvider(): IMessageProvider {
        return this._loadMessageProvider;
    }

    get bLoaded(): boolean {
        return this._bLoaded;
    }

    get OnLoadComplete(): Promise<void> {
        const self = this;

        if (self._bLoaded) return Promise.resolve();

        if (!_.isEmpty(self._OnLoadComplete)) return self._OnLoadComplete;

        const returnValue = (self._OnLoadComplete = (async () => {
            let msg = "";

            self._loadMessageProvider.clearMessages();
            self.planRow = self.dbContext.get(esWB_Plan, self.wbPlanId)
            if (_.isEmpty(self.planRow))
                self.planRow = await self.dbContext.find(esWB_Plan, self.wbPlanId)

            if (_.isEmpty(self.planRow)) {
                msg = `Unknown Plan ${self.wbPlanId}`;
                self.LoadMessageProvider.addMessage({
                    code: "MissingPlan",
                    type: MessageType.Error,
                    title: msg,
                    description: msg,
                });
                return;
            }

            let planToRow = await self.dbContext.find(esWB_Customer, self.planRow.cif.WB_CUST_ID_PLANNING)
            let planToNode = self.addNode(planToRow, 'PlanTo')
            self.arrTopLevelNodes.push(planToNode)

            // Get the Source Customers
            const aSourceCustomerService = new SourceCustomerService();
            let arrSourceCustomerIds = await aSourceCustomerService.getSrcCustIdsbyParent([self.planRow.cif.WB_CUST_ID_PLANNING]);
            let arrSourceCustomers: WB_CustomerRow[] = []
            if(!_.isEmpty(arrSourceCustomerIds)) {
                arrSourceCustomers = await self.dbContext.findAll(esWB_Customer, {
                    where: { WB_CUST_ID: { inq: arrSourceCustomerIds } }
                });
            }
            _.forEach(arrSourceCustomers, (srcCust) => {
                let childNode = self.addNode(srcCust, 'Source')
                const nodeIdChild = childNode.NodeId;
                const nodeIdParent = planToNode.NodeId;


                this.dctNodeParentByChildId[nodeIdChild] = planToNode;
                childNode.ParentId = planToNode.NodeId;

                if (nodeIdParent in this.dctNodeChildrenByParentId) this.dctNodeChildrenByParentId[nodeIdParent].push(childNode);
                else this.dctNodeChildrenByParentId[nodeIdParent] = [childNode];

            })

            // Add Static Levels
            self.dctHierarchyLevels['PlanTo'] = {
                LevelId: 'PlanTo',
                LevelName: 'Planning Customer',
                LevelDepth: 1
            }

            self.dctHierarchyLevels['Source'] = {
                LevelId: 'Source',
                LevelName: 'Source Customer',
                LevelDepth: 2
            }

            //


            self._bLoaded = true;
        })());

        return returnValue;
    }



    private addNode(aCustRow: WB_CustomerRow, sLevel: string): IHierarchyNode {
        //--
        if (_.isEmpty(aCustRow)) return undefined;

        const wbCustId = aCustRow.cif.WB_CUST_ID + "";
        const levelId = sLevel;

        if (wbCustId in this.dctNodes) return this.dctNodes[wbCustId];

        const newNode: IHierarchyNode = {
            NodeId: wbCustId,
            NodeIdUI: aCustRow.cif.CUSTOMER_ID,
            NodeName: aCustRow.cif.CUSTOMER_NAME,
            NodeLevel: sLevel,
            NodeOrder: aCustRow.cif.CUSTOMER_NAME,
            NodeShortName: aCustRow.cif.CUSTOMER_NAME,
        };

        this.dctNodes[wbCustId] = newNode;

        if (levelId in this.dctNodesByLevel) this.dctNodesByLevel[levelId].push(newNode);
        else this.dctNodesByLevel[levelId] = [newNode];

        return this.dctNodes[wbCustId];
    }

    getLevels(): IHierarchyLevel[] {
        return _.values(this.dctHierarchyLevels);
    }

    getTopLevelNodes(): IHierarchyNode[] {
        return this.arrTopLevelNodes;
    }

    getNode(NodeId: string): IHierarchyNode {
        if (NodeId in this.dctNodes) return this.dctNodes[NodeId];

        return undefined;
    }

    getNodes(): IHierarchyNode[] {
        return _.values(this.dctNodes);
    }

    getNodesAtLevel(HierarchyLevel: string): IHierarchyNode[] {
        if (HierarchyLevel in this.dctNodesByLevel) return this.dctNodesByLevel[HierarchyLevel];

        return [];
    }

    getChildren(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string, levelId?: string): IHierarchyNode[] {
        const arrNodeIds = this.convertToKeyArray(nodes);

        const bCheckLevel = !_.isEmpty(levelId);
        const returnValue: IHierarchyNode[] = [];

        const arrRecurseOnChildren: IHierarchyNode[] = [];
        _.forEach(arrNodeIds, (aNodeId) => {
            if (aNodeId in this.dctNodeChildrenByParentId) {
                const arrChildren = this.dctNodeChildrenByParentId[aNodeId];
                _.forEach(arrChildren, (aChildNode) => {
                    if (bCheckLevel) {
                        if (levelId == aChildNode.NodeLevel) returnValue.push(aChildNode);
                        else arrRecurseOnChildren.push(aChildNode);
                    } else {
                        returnValue.push(aChildNode);
                    }
                });
            }
        });

        // Recurse
        if (bCheckLevel && !_.isEmpty(arrRecurseOnChildren)) {
            const moreChildren = this.getChildren(arrRecurseOnChildren, levelId);
            _.forEach(moreChildren, (a) => returnValue.push(a));
        }

        return returnValue;
    }

    getParents(
        nodes: IHierarchyNode[] | IHierarchyNode | string[] | string,
        levelId?: string,
    ): {
        [NodeId: string]: IHierarchyNode;
    } {
        const arrNodeIds = this.convertToKeyArray(nodes);

        const bCheckLevel = !_.isEmpty(levelId);
        const returnValue: { [NodeId: string]: IHierarchyNode } = {};

        const heightLimit = 20;
        _.forEach(arrNodeIds, (aNodeId) => {
            let depth = 0;
            let parent = this.dctNodeParentByChildId[aNodeId];
            if (bCheckLevel) {
                while (parent && parent?.NodeLevel != levelId && depth < heightLimit) {
                    parent = this.dctNodeParentByChildId[parent.NodeId];
                    depth += 1;
                }
                if (depth == heightLimit) parent = undefined;
            }

            returnValue[aNodeId] = parent;
        });

        return returnValue;
    }

    private convertToKeyArray(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string): string[] {
        const returnValue: string[] = [];

        if (_.isEmpty(nodes) && !_.isNumber(nodes)) return returnValue;

        let keyList: any[];
        if (_.isArray(nodes)) keyList = nodes;
        else keyList = [nodes];

        _.forEach(keyList, (aKeyItem) => {
            if (_.isString(aKeyItem)) {
                returnValue.push(aKeyItem);
                return;
            }

            if (_.isNumber(aKeyItem)) returnValue.push(aKeyItem + "");

            if (_.isEmpty(aKeyItem)) return;

            if ("NodeId" in aKeyItem) returnValue.push(aKeyItem.NodeId);
        });

        return returnValue;
    }
}
